import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { observer } from 'mobx-react-lite';
import { workflowSubTask } from '../../app/api/types';

interface LabelModalProps {
  openModal: boolean;
  setOpenModal: (value: boolean) => void;
  shippingPrep: workflowSubTask;
  data: any;
}

const LabelModal: React.FC<LabelModalProps> = ({ openModal, setOpenModal, shippingPrep, data }) => {
  const { activityStore } = useStore();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true)
    let parsedData;

    // Only proceed if we have Package Information
    const packageInfo = shippingPrep.elements.find(element => element.elementName === 'Package Information');
    if (packageInfo?.elementValue) {
      // Safely parse the elementValue if it exists
      let modifiedString = packageInfo.elementValue.replace(/^DynamicTable\|/, '');
      try {
        parsedData = JSON.parse(modifiedString);

        // If parsedData is available, loop through each item and call Fedex API
        const apiCalls = parsedData.map((item: any) => {
          let payload = {
            shippingId: data.id,
            address: data.address,
            account: data.account,
            packageNo: Number(item['Package No']),
            weight: item.Weight,
            dimension: '47 x 35 x 26'
          };

          return activityStore.Fedex(payload); // This will be called once for each item in parsedData
        });

        // Wait for all API calls to complete
        Promise.all(apiCalls).then(() => {
          setIsLoading(false); // Set loading to false once all API calls are done
        });

      } catch (error) {
        console.error('Invalid JSON:', error);
        setIsLoading(false); // Set loading to false in case of error
      }
    } else {
      setIsLoading(false); // Set loading to false if no package info is found
    }

  }, [shippingPrep.elements, data, activityStore]); // Dependency array ensures effect runs on shippingPrep.elements change

  const handlePrint = () => {
    if (activityStore.imageSrc.length > 0) {
      const printWindow = window.open('', '', 'height=500,width=800');
      if (printWindow) {
        printWindow.document.write('<html><head><title>Print Label</title>');
        printWindow.document.write('<style>');
        printWindow.document.write(`
          body {
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
            height: 90vh;
          }
          .image-container {
            width: 100%;
            height: 80%;
            display: flex;
            justify-content: center;
            align-items: center;
            page-break-after: always;  // Ensure a page break after each image
            padding-bottom: 20px;
          }
          img {
            width: 100%;
            display: block;
            margin: 0 auto;
            transform: rotate(180deg);  // Apply the 180-degree rotation for printing
          }
          @media print {
            body {
              display: block;
            }
            .image-container {
              page-break-after: always;  // Ensure a page break after each image when printing
            }
          }
        `);
        printWindow.document.write('</style>');
        printWindow.document.write('</head><body>');
        
        // Iterate over the images and write them with a page break after each
        activityStore.imageSrc.forEach((src, index) => {
          printWindow.document.write('<div class="image-container">');
          printWindow.document.write('<img src="' + src.imageUrl + '" alt="Generated Image" />');
          printWindow.document.write('</div>');  // Image container
        });
  
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();  // Trigger the print dialog
      }
    }
  };

  return (
    <Modal open={openModal} >
      <Modal.Header>Generate Label</Modal.Header>
      <Modal.Content>
        {isLoading ? (
           <div
           style={{
             display: 'flex',
             justifyContent: 'center',
             alignItems: 'center',
             height: '100%',  // Ensure the container takes full height of the modal content
           }}
         >
           <div className="custom-loader"/>
         </div>
        ) : (
          <div style={{ width: '100%', height: '100%'}}>
            {activityStore.imageSrc.map((src, index) => (
              <img
                key={src.packageNumber}
                style={{ width: '100%', height: '100%', transform: 'rotate(180deg)', border: '1px solid', padding: '30px', marginBottom: '30px' }}
                src={src.imageUrl}
                alt={`Generated from ZPL ${index + 1}`}
              />
            ))}
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        {/* Close the modal */}
        <Button color="red" onClick={() => setOpenModal(false)}>
          Close
        </Button>
        <Button color="blue" onClick={handlePrint} disabled={isLoading}>
          Print Label
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default observer(LabelModal);